import { useImperativeHandle, useRef } from 'react';

/**
 * Hook to expose flowchart methods via ref
 * Centralizes all imperative API methods in one place
 * 
 * Uses refs for state values to avoid stale closure issues when
 * methods are called via the imperative ref.
 */
const useFlowchartRef = (ref, {
  // Layout & view controls
  handleAutoLayout,
  handleAutoLayoutInstant,
  clearCustomPositionsForNodeIds,
  getNodePositions,
  getNodeDimensions,
  handleResetView,
  handleGoToStart,
  handleZoomIn,
  handleZoomOut,
  zoom,
  snapToGrid,
  setSnapToGrid,
  
  // Viewport utilities
  getViewportCenter,
  findEmptySpaceInViewport,
  findPositionInCampaign,
  setTaskPosition,
  followTaskAfterLayout,
  
  // Focus mode
  handleFocusTask,
  handleFocusTasks,
  focusSelectedTasks,
  centerOnTask,
  panToPosition,
  centerOnSelected,
  handleExitFocusMode,
  isFocusMode,
  focusedTaskIds,
  // Instant focus mode (no animation - for view switching)
  focusTasksInstant,
  exitFocusModeInstant,
  
  // Selection
  selectedNodes,
  deleteSelectedNodes,

  // Node creation
  openAddMilestonePrompt,
}) => {
  // Use refs to always get latest values in imperative methods
  const selectedNodesRef = useRef(selectedNodes);
  selectedNodesRef.current = selectedNodes;
  
  const isFocusModeRef = useRef(isFocusMode);
  isFocusModeRef.current = isFocusMode;
  
  const focusedTaskIdsRef = useRef(focusedTaskIds);
  focusedTaskIdsRef.current = focusedTaskIds;
  
  useImperativeHandle(ref, () => ({
    // Layout controls
    autoLayout: handleAutoLayout,
    autoLayoutInstant: handleAutoLayoutInstant,
    clearCustomPositionsForNodeIds,
    getNodePositions: () => getNodePositions?.() ?? {},
    getNodeDimensions: () => getNodeDimensions?.() ?? {},
    resetView: handleResetView,
    goToStart: handleGoToStart,
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    
    // State getters
    getZoom: () => zoom,
    getSnapToGrid: () => snapToGrid,
    toggleSnapToGrid: () => setSnapToGrid(prev => !prev),
    
    // Viewport utilities
    getViewportCenter,
    findEmptySpaceInViewport,
    findPositionInCampaign,
    setTaskPosition,
    followTaskAfterLayout,
    
    // Focus mode
    focusTask: handleFocusTask,
    focusTasks: (taskIds, isFilterMode = false) => handleFocusTasks(taskIds, isFilterMode),
    focusSelectedTasks,
    centerOnTask,
    panToPosition,
    centerOnSelected,
    exitFocusMode: handleExitFocusMode,
    isFocusMode: () => isFocusModeRef.current,
    getFocusedTaskIds: () => focusedTaskIdsRef.current,
    // Instant focus mode (no animation - for view switching)
    focusTasksInstant,
    exitFocusModeInstant,
    
    // Selection - use refs to get latest values
    getSelectedNodes: () => selectedNodesRef.current,
    hasSelection: () => selectedNodesRef.current.size > 0,
    deleteSelectedNodes,
  }), [
    handleAutoLayout,
    handleAutoLayoutInstant,
    clearCustomPositionsForNodeIds,
    getNodePositions,
    getNodeDimensions,
    handleResetView,
    handleGoToStart,
    handleZoomIn,
    handleZoomOut,
    zoom,
    snapToGrid,
    setSnapToGrid,
    getViewportCenter,
    findEmptySpaceInViewport,
    findPositionInCampaign,
    setTaskPosition,
    followTaskAfterLayout,
    handleFocusTask,
    handleFocusTasks,
    focusSelectedTasks,
    centerOnTask,
    panToPosition,
    centerOnSelected,
    handleExitFocusMode,
    deleteSelectedNodes,
    focusTasksInstant,
    exitFocusModeInstant,
  ]);
};

export default useFlowchartRef;

